import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ThemeToggle";
import type { Domain } from "@/lib/types";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
];

export function Header() {
  const location = useLocation();
  const [domains, setDomains] = useState<Domain[]>([]);

  useEffect(() => {
    fetch("/api/domains").then((r) => r.json()).then(setDomains);
  }, []);

  const isActive = (to: string) => location.pathname === to;
  const isDomainsActive = location.pathname.startsWith("/domains");

  const linkClass = (active: boolean) =>
    cn(
      "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
      active
        ? "bg-primary text-primary-foreground"
        : "text-muted-foreground hover:text-foreground hover:bg-accent",
    );

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-6">
        <Link
          to="/"
          className="flex items-center gap-2.5 font-display text-lg font-bold tracking-tight"
        >
          <img src="/assets/lgs-mark.png" alt="" className="h-9 w-9 dark:invert" />
          <span>Loose Goose Systems</span>
        </Link>
        <nav className="flex items-center gap-1">
          {navLinks.map((link) => (
            <Link key={link.to} to={link.to} className={linkClass(isActive(link.to))}>
              {link.label}
            </Link>
          ))}
          <div
            className={cn(
              "flex items-center rounded-lg overflow-hidden",
              isDomainsActive ? "bg-primary text-primary-foreground" : "",
            )}
          >
            <Link
              to="/domains"
              className={cn(
                "pl-4 pr-2 py-2 text-sm font-medium transition-colors",
                isDomainsActive
                  ? "text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent",
              )}
            >
              Domains
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label="Open domains menu"
                className={cn(
                  "pr-3 pl-1 py-2 transition-colors cursor-pointer",
                  isDomainsActive
                    ? "text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent",
                )}
              >
                <ChevronDown className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {domains.map((d) => (
                  <DropdownMenuItem key={d.slug} asChild>
                    <Link to={`/domains/${d.slug}`} className="flex items-center gap-2 w-full">
                      {d.icon && <span>{d.icon}</span>}
                      <span>{d.name}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="ml-1 border-l border-border pl-1">
            <ThemeToggle />
          </div>
        </nav>
      </div>
    </header>
  );
}
